import React, { useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useProfileAccess } from '@yada/ui';
import { getProfileIcon, getInitials, getProfileColor, getToolIdFromPath } from './profileDisplayUtils';
import { ProfilePopover } from './ProfilePopover';
import { useProfileContext } from '../context/ProfileContext';
import { useProfiles } from './useProfiles';
import styles from './ProfileButton.module.css';

interface ProfileButtonProps {
    onManageProfiles?: () => void;
    onRequestSwitch?: (toProfileId: string) => void;
}

export const ProfileButton: React.FC<ProfileButtonProps> = ({ onManageProfiles, onRequestSwitch }) => {
    const location = useLocation();
    const { activeProfile: currentProfile, profiles } = useProfileContext();
    const { switchProfile } = useProfiles();
    const [isOpen, setIsOpen] = useState(false);
    const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
    const buttonRef = useRef<HTMLButtonElement>(null);

    const context = {
        toolId: getToolIdFromPath(location.pathname),
        workspaceId: undefined,
        viewPath: location.pathname,
    };
    const accessibleProfiles = useProfileAccess(profiles, context);
    const inaccessibleProfiles = profiles.filter(p => !accessibleProfiles.some(ap => ap.id === p.id));

    const handleOpen = () => {
        const rect = buttonRef.current?.getBoundingClientRect();
        if (rect) {
            setPopoverStyle({
                bottom: window.innerHeight - rect.top + 8,
                right: window.innerWidth - rect.right,
            });
        }
        setIsOpen(true);
    };

    const handleSwitch = async (profileId: string) => {
        if (profileId === currentProfile?.id) return;
        if (onRequestSwitch) {
            onRequestSwitch(profileId);
            return;
        }
        try {
            await switchProfile(profileId);
        } catch (err) {
            console.error('Failed to switch profile:', err);
        }
    };

    if (!currentProfile) return null;

    const Icon = getProfileIcon(currentProfile.profileType);
    const isHousehold = currentProfile.profileType === 'household';
    const avatar = currentProfile.metadata?.avatar as string | undefined;
    const initials = getInitials(currentProfile.name);
    const color = getProfileColor(currentProfile.id, 0);

    return (
        <>
            <button
                ref={buttonRef}
                className={styles.button}
                onClick={handleOpen}
                title={currentProfile.name}
                aria-label={`Profile: ${currentProfile.name}`}
                aria-expanded={isOpen}
            >
                {avatar ? (
                    <img src={avatar} alt="" className={styles.avatarImg} />
                ) : isHousehold ? (
                    <Icon size={16} className={styles.icon} />
                ) : (
                    <div className={styles.avatar} style={{ backgroundColor: color }}>
                        <span className={styles.initials}>{initials}</span>
                    </div>
                )}
                {!isHousehold && <Icon size={8} className={styles.typeIcon} />}
            </button>
            {isOpen && (
                <ProfilePopover
                    currentProfile={currentProfile}
                    accessibleProfiles={accessibleProfiles}
                    inaccessibleProfiles={inaccessibleProfiles}
                    onSwitch={handleSwitch}
                    onManageProfiles={onManageProfiles ?? (() => {})}
                    onClose={() => setIsOpen(false)}
                    style={popoverStyle}
                />
            )}
        </>
    );
};
